import { Button, Typography, Card } from '../../components/primitives';
import { CartTermsAndConditions } from '../../components/text/CartTermsAndConditions';
import { LargeOrderServicesAlert } from '../../components/text/LargeOrderServicesAlert';
import Alert from '../../components/ui/Alert';
import { useBlurbContext } from '../../context/Blurb.context';
import { useCartHook } from '../../hooks/cartHook';
import { testId } from '../../utils';
import { LARGE_ORDER_THRESHOLD } from '../../utils/constants';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RenderPropSticky } from 'react-sticky-el';

type CartSummaryProps = {
  'data-testid': string;
  isLoading: boolean;
  collapsed?: boolean;
  isFixed?: boolean;
};

const CartSummary: React.FC<CartSummaryProps> = ({
  'data-testid': dataTestId,
  isLoading,
  collapsed,
  isFixed,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.cart' });
  const { cart, maxQuantityProductClass } = useCartHook();
  const { currencyFormatter } = useBlurbContext();
  const handleClick = () => {
    navigate('/user/checkout');
  };

  const cartHasFirstTimeOrder = React.useMemo(() => {
    const lineItems = cart?.lineItems ?? [];
    return lineItems.some(
      (item: Cornerstone.CheckoutLineItem) =>
        // !item.ordered && (item.quantity ?? 0) > 1,
        // Ordered information should not be part of project OR cart. This data should come from the order API.
        (item.quantity ?? 0) > 1 && !item.projectWasOrdered,
    );
  }, [cart?.lineItems]);

  const cartHasLargeOrder = React.useMemo(() => {
    if (maxQuantityProductClass) {
      return maxQuantityProductClass.quantity > LARGE_ORDER_THRESHOLD;
    }
  }, [maxQuantityProductClass]);

  return (
    <div className="flex flex-col p-0 md:p-6">
      <div className="space-y-4 mb-4">
        {!isFixed && (
          <div className="flex justify-between space-y-2">
            <Typography variant="title-small" className="text-gray-soft">
              {t('order_summary_title')}
            </Typography>
          </div>
        )}

        {!collapsed && !isFixed && (
          <>
            <div className="flex justify-between">
              <Typography variant="body">{t('subtotal')}</Typography>
              {cart?.subtotal && (
                <Typography variant="body" {...testId(dataTestId, 'subtotal')}>
                  {currencyFormatter(cart?.subtotal)}
                </Typography>
              )}
            </div>

            {cart?.lineItemDiscountTotal ? (
              <div className="flex justify-between">
                <Typography variant="body">{t('discount')}</Typography>
                <Typography
                  className="text-green-dark"
                  variant="body"
                  {...testId(dataTestId, 'discount')}
                >
                  {currencyFormatter({
                    amount: cart.lineItemDiscountTotal.amount * -1,
                    currency: cart.lineItemDiscountTotal.currency,
                  })}
                </Typography>
              </div>
            ) : null}
            <hr />
          </>
        )}

        <div className="flex justify-between">
          <Typography variant="body-bold">{t('estimated_total')}</Typography>
          {cart?.total && (
            <Typography
              variant="body-bold"
              {...testId(dataTestId, 'estimated_total')}
            >
              {currencyFormatter(cart.total)}
            </Typography>
          )}
        </div>

        {!collapsed && <hr />}
      </div>

      {!collapsed && (
        <div className="space-y-6">
          <CartTermsAndConditions />

          <div className="space-y-2">
            {cartHasLargeOrder && <LargeOrderServicesAlert />}
            {cartHasFirstTimeOrder && (
              <Alert variant="error" {...testId(dataTestId, 'first-time')}>
                {t('first_order_proof_rec')}
              </Alert>
            )}
          </div>
          <Button
            {...testId(dataTestId, 'cart-checkout')}
            onClick={handleClick}
            disabled={isLoading}
          >
            {t('checkout')}
          </Button>
        </div>
      )}
    </div>
  );
};

type CartDetailsProps = {
  isLoading: boolean;
};

export const CartDetails: React.FC<CartDetailsProps> = ({ isLoading }) => {
  return (
    <div className="md:w-[520px]">
      <Card {...testId('summary')}>
        <CartSummary {...testId('summary')} isLoading={isLoading} />
      </Card>
    </div>
  );
};

type MobileCartSheetProps = CartDetailsProps & {
  isFixed: boolean;
};

export const MobileCartSheet: React.FC<MobileCartSheetProps> = ({
  isLoading,
  isFixed,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const scrolledDown = window.scrollY > lastScrollY;
      lastScrollY = window.scrollY;

      setCollapsed(scrolledDown);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [setCollapsed]);

  return (
    <div ref={contentRef}>
      <CartSummary
        {...testId('mobile-summary')}
        isLoading={isLoading}
        collapsed={collapsed && isFixed}
        isFixed={isFixed}
      />
    </div>
  );
};

export const MobileCartDetails: React.FC<CartDetailsProps> = ({
  isLoading,
}) => {
  return (
    <RenderPropSticky
      {...testId('mobile-cart-details')}
      mode="bottom"
      dontUpdateHolderHeightWhenSticky={true}
      hideOnBoundaryHit={true}
      positionRecheckInterval={100}
      scrollElement="window"
    >
      {({ isFixed, wrapperStyles, wrapperRef, holderStyles, holderRef }) => (
        <div
          {...testId('mobile-cart-details')}
          ref={holderRef}
          style={holderStyles}
        >
          <div
            ref={wrapperRef}
            style={{
              ...wrapperStyles,
              width: '100%',
              left: 0,
              boxShadow: isFixed
                ? '0px -4px 8px 0px rgba(51, 51, 51, 0.15)'
                : '',
            }}
            className="bg-neutral-100 p-5 ease-in-out transition-all shadow duration-100"
          >
            <MobileCartSheet isLoading={isLoading} isFixed={isFixed} />
          </div>
        </div>
      )}
    </RenderPropSticky>
  );
};
